import { io } from 'socket.io-client'
import { MAIN_SOCKET_API_CONFIG } from '../constants/api/mainSocket'
import { IS_DEV_ENV } from '../constants/general'
import { socketAckErrorToError } from '../modules/chat/socketApiErrors'
import AuthService from '../services/Auth'

const SOCKET_LOG_PREFIX = '[mainSocket]'

const wireDevSocketLifecycleLogs = socket => {
  if (!IS_DEV_ENV || !socket) return
  socket.on('connect', () => {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug(SOCKET_LOG_PREFIX, 'connected', { id: socket.id })
    }
  })
  socket.on('disconnect', reason => {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug(SOCKET_LOG_PREFIX, 'disconnected', { reason })
    }
  })
  socket.on('connect_error', err => {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug(SOCKET_LOG_PREFIX, 'connect_error', err?.message || String(err))
    }
  })
  socket.on('reconnect_attempt', attempt => {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug(SOCKET_LOG_PREFIX, 'reconnect_attempt', { attempt })
    }
  })
}

let socketInstance = null
let lastAuthToken = null
let isSocketAuthenticated = false
const onConnectHandlers = []
const onAuthenticatedHandlers = []
let pendingSends = [] // queue for resend-on-reconnect: [{ event, payload }]
/** Waiters for authentication::verifyToken ({ resolve, reject }). */
let authReadyWaiters = []
/** Single in-flight connection promise so concurrent connectMainSocket() calls share one connection */
let connectPromise = null

const settleAuthWaiters = (error = null) => {
  const waiters = authReadyWaiters
  authReadyWaiters = []
  const err = error != null ? (error instanceof Error ? error : new Error(String(error))) : null
  for (const waiter of waiters) {
    if (err) waiter.reject(err)
    else waiter.resolve()
  }
}

/** Wait until `authentication::verifyToken` has succeeded on the current socket. */
export const waitForMainSocketAuthenticated = (timeoutMs = 30000) => {
  if (isSocketAuthenticated) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const idx = authReadyWaiters.indexOf(waiter)
      if (idx >= 0) authReadyWaiters.splice(idx, 1)
      reject(new Error('Socket authentication timeout'))
    }, timeoutMs)
    const waiter = {
      resolve: () => {
        clearTimeout(timer)
        resolve()
      },
      reject: err => {
        clearTimeout(timer)
        reject(err)
      }
    }
    authReadyWaiters.push(waiter)
  })
}

/** Socket.IO disconnect reasons that keep the same manager reconnecting. */
const isTransientDisconnect = reason =>
  reason === 'transport close' ||
  reason === 'ping timeout' ||
  reason === 'transport error'

const parseJwtPayload = token => {
  if (!token || typeof token !== 'string') return null
  const rawToken = token.startsWith('Bearer ') ? token.slice('Bearer '.length) : token
  const parts = rawToken.split('.')
  if (parts.length < 2) return null

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(payload)
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

const parseJwtExpiration = token => {
  const data = parseJwtPayload(token)
  if (!data || typeof data.exp !== 'number') return null
  return new Date(data.exp * 1000).toISOString()
}

const parseJwtUserId = token => {
  const data = parseJwtPayload(token)
  if (!data) return null
  return data.user_id || data.userId || data.sub || data.id || null
}

const getAuthSnapshot = () => {
  const token = AuthService.GetAuthToken()
  if (!token) return null

  const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`
  const expires = parseJwtExpiration(bearerToken)
  const userId = parseJwtUserId(bearerToken)

  return {
    token,
    bearerToken,
    expires,
    userId
  }
}

/**
 * Build auth object for socket.io handshake using the locally stored auth token (from standard login).
 * Server receives it as socket.handshake.auth. Sent under auth.wss.token for server path packet.socket.wss.token.
 */
const buildAuth = authSnapshot => {
  if (!authSnapshot) return {}
  const { bearerToken, expires, userId } = authSnapshot
  return {
    token: bearerToken,
    authorization: bearerToken,
    expires,
    wss: {
      token: bearerToken,
      expires,
      user_id: userId
    }
  }
}

const emitVerifyToken = (socket, authSnapshot) => {
  if (!socket || !authSnapshot) return
  const rawToken = authSnapshot.token.startsWith('Bearer ')
    ? authSnapshot.token.slice('Bearer '.length)
    : authSnapshot.token

  socket.emit('authentication::verifyToken', { auth_token: rawToken, device: 'browser' }, (error) => {
    if (error) {
      isSocketAuthenticated = false
      const authError = typeof error === 'number' || (error != null && typeof error === 'object')
        ? socketAckErrorToError(error)
        : (error instanceof Error ? error : new Error(String(error)))
      settleAuthWaiters(authError)
      if (IS_DEV_ENV && typeof console !== 'undefined' && console.warn) {
        console.warn(SOCKET_LOG_PREFIX, 'authentication::verifyToken failed', authError.message)
      }
      socket.disconnect()
      return
    }

    isSocketAuthenticated = true
    settleAuthWaiters()
    const queue = pendingSends
    pendingSends = []
    queue.forEach(({ event, payload }) => {
      socket.emit(event, payload)
    })
    onAuthenticatedHandlers.forEach(handler => {
      try {
        handler(socket)
      } catch (err) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn(SOCKET_LOG_PREFIX, 'onAuthenticated handler error', err)
        }
      }
    })
  })
}

const wireAuthenticationExpired = (socket, authSnapshot) => {
  if (!socket) return
  socket.off('authentication expired')
  socket.on('authentication expired', () => {
    isSocketAuthenticated = false
    if (typeof console !== 'undefined' && console.debug) {
      console.debug(SOCKET_LOG_PREFIX, 'authentication expired — re-verifying token')
    }
    emitVerifyToken(socket, authSnapshot)
  })
}

const wireLegacyAuthHandshake = (socket, authSnapshot) => {
  if (!socket) return
  socket.on('connected', () => emitVerifyToken(socket, authSnapshot))
  socket.on('connect', () => emitVerifyToken(socket, authSnapshot))
}

export const connectMainSocket = async () => {
  const authSnapshot = getAuthSnapshot()

  if (!authSnapshot) {
    if (socketInstance) {
      socketInstance.disconnect()
      socketInstance = null
    }
    connectPromise = null
    lastAuthToken = null
    return null
  }

  const token = authSnapshot.bearerToken

  // Reuse existing connected socket when token unchanged
  if (socketInstance && socketInstance.connected && token === lastAuthToken) {
    return socketInstance
  }

  // Reuse in-flight connection when same token (avoids multiple sockets from concurrent calls)
  if (connectPromise && token === lastAuthToken) {
    return connectPromise
  }

  // Token changed or first connect: ensure any previous socket is discarded
  if (socketInstance) {
    socketInstance.disconnect()
    socketInstance = null
  }

  const auth = buildAuth(authSnapshot)

  connectPromise = new Promise((resolve, reject) => {
    const url = MAIN_SOCKET_API_CONFIG.URL || ''
    const hasPath = url.includes('/socket.io')
    const baseUrl = hasPath ? url.replace(/\/socket\.io\/?$/, '') || url : url
    const opts = {
      transports: ['polling', 'websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      auth,
      query: {
        token: auth.authorization,
        expires: auth.expires,
        'wss[token]': auth.authorization,
        'wss[expires]': auth.expires,
        'wss[user_id]': auth.wss.user_id,
        uuid_device: 'operator'
      }
    }
    if (hasPath) {
      opts.path = '/socket.io'
    }
    const socket = io(baseUrl, opts)

    socketInstance = socket
    lastAuthToken = token

    wireDevSocketLifecycleLogs(socket)
    wireLegacyAuthHandshake(socket, authSnapshot)
    wireAuthenticationExpired(socket, authSnapshot)

    socket.on('error', err => {
      if (process.env.NODE_ENV === 'development' && err?.message === 'Invalid namespace') {
        console.warn('[mainSocket] Invalid namespace (server may be sending namespaced packets); connection may be limited.')
      }
    })

    socket.on('connect', () => {
      isSocketAuthenticated = false
      onConnectHandlers.forEach(handler => handler(socket))
      resolve(socket)
    })

    socket.on('connect_error', err => {
      connectPromise = null
      reject(err)
    })

    socket.on('disconnect', reason => {
      isSocketAuthenticated = false
      settleAuthWaiters(new Error('Socket disconnected'))
      // Keep socketInstance during auto-reconnect so listeners and getMainSocket() stay valid.
      if (!isTransientDisconnect(reason)) {
        socketInstance = null
        connectPromise = null
        lastAuthToken = null
      }
    })
  })
    .then(socket => {
      connectPromise = null
      return socket
    })
    .catch(err => {
      connectPromise = null
      socketInstance = null
      throw err
    })

  return connectPromise
}

export const getMainSocket = () => socketInstance

const LOGOUT_DEVICE_ACK_TIMEOUT_MS = 3000

/** Revoke server session via frozen mobile contract, then disconnect. */
export const logoutMainSocket = () => new Promise(resolve => {
  const socket = socketInstance
  if (!socket || !socket.connected) {
    disconnectMainSocket()
    resolve()
    return
  }

  let settled = false
  const finish = () => {
    if (settled) return
    settled = true
    disconnectMainSocket()
    resolve()
  }

  const timeout = setTimeout(finish, LOGOUT_DEVICE_ACK_TIMEOUT_MS)
  socket.emit('authentication::logoutDevice', {}, () => {
    clearTimeout(timeout)
    finish()
  })
})

export const disconnectMainSocket = () => {
  if (socketInstance) {
    socketInstance.disconnect()
    socketInstance = null
  }
  connectPromise = null
  lastAuthToken = null
  isSocketAuthenticated = false
  authReadyWaiters = []
}

export const onSocketConnect = handler => {
  onConnectHandlers.push(handler)
}

/** Fired after `authentication::verifyToken` succeeds (initial connect and reconnect). */
export const onSocketAuthenticated = handler => {
  onAuthenticatedHandlers.push(handler)
}

export const isMainSocketAuthenticated = () => isSocketAuthenticated

export const queueEmitUntilConnected = (event, payload) => {
  if (socketInstance && socketInstance.connected && isSocketAuthenticated) {
    socketInstance.emit(event, payload)
    return
  }
  pendingSends.push({ event, payload })
}

export default {
  connectMainSocket,
  getMainSocket,
  logoutMainSocket,
  disconnectMainSocket,
  onSocketConnect,
  onSocketAuthenticated,
  isMainSocketAuthenticated,
  waitForMainSocketAuthenticated,
  queueEmitUntilConnected
}
